import { isTravelTypeWithHousing } from '@repo/utils'
import React from 'react'
import { useIntl } from 'react-intl'

import AdminActionButton from '../admin-action/AdminActionButton'
import { AdminActionEnum } from '../admin-action/utils/admin-action-data'
import UserPaymentButton from '../user-payment-button/UserButtonPayment'
import PingPongButton from '../../buttons-by-step/PingPongButton'
import { PingPongButtonTypeEnum } from '../../utils/ping-pong-button-type.enum.ts'
import {
  ClientProposalOutputDto,
  ClientRequestOutputDto,
  NonCompliantRulesFragment,
  Payment_Type,
} from '../../../../../../lib/graphql'
import { useTravelTypeContext } from '../../../../../contexts'
import { IProposalInteractionsProps } from '../../../Proposal'

export interface IUserCanPayBalanceFooterProps {
  adminActions: IProposalInteractionsProps['adminActions']
  handleBalancePayment?: () => void
  clientRequest: Pick<ClientRequestOutputDto, '_id' | 'adminCsm' | 'balance'>
  clientProposal: NonCompliantRulesFragment &
    Pick<
      ClientProposalOutputDto,
      | '_id'
      | 'userCurrentAction'
      | 'clientRequestId'
      | 'houseId'
      | 'permissions'
    >
}

const UserCanPayBalanceFooter = ({
  adminActions,
  handleBalancePayment,
  clientProposal,
  clientRequest,
}: IUserCanPayBalanceFooterProps) => {
  const intl = useIntl()
  const { travelType } = useTravelTypeContext()

  const adminSkippedBalancePayment = adminActions?.adminSkippedBalancePayment
  const adminCanceledAdjustmentsConfirmation =
    adminActions?.adminCanceledAdjustmentsConfirmation

  if (!clientRequest.balance?.status) {
    throw new Error('clientRequest.balance.status is required')
  }

  const buttonLabel = !isTravelTypeWithHousing(travelType)
    ? intl.formatMessage({
        id: 'ui.userCurrentActionButton.userCanPayBalance.button.bookingConfirmedByOwner',
        defaultMessage: 'Réservation confirmée par le prestataire',
      })
    : intl.formatMessage({
        id: 'ui.userCurrentActionButton.userCanPayBalance.button.bookingConfirmedByServiceProvider',
        defaultMessage: "Réservation confirmée par l'hôte",
      })

  return (
    <>
      <div className='flex w-full flex-col gap-3 py-5'>
        <PingPongButton
          label={buttonLabel}
          type={PingPongButtonTypeEnum.CONFIRMED_BY_OWNER}
          hideIcon={true}
        />

        <UserPaymentButton
          clientRequestId={clientRequest._id}
          houseId={clientProposal.houseId}
          payment={{
            paymentType: Payment_Type.BALANCE,
            paymentAmount:
              clientRequest.balance?.price?.totalUser?.priceWithDiscountAndFees
                ?.priceWithVat ?? 0,
            paymentStatus: clientRequest.balance?.status,
          }}
          handlePayment={handleBalancePayment}
        />

        {adminCanceledAdjustmentsConfirmation && (
          <AdminActionButton
            adminAction={AdminActionEnum.CANCEL_ADJUSTMENTS_CONFIRMATION}
            onClickConfirm={() =>
              adminCanceledAdjustmentsConfirmation.callback({
                clientProposalId: clientProposal._id,
              })
            }
            isLoading={adminCanceledAdjustmentsConfirmation.isLoading}
          />
        )}

        {clientProposal.permissions.canValidateStepFiveWithoutBalancePayment &&
          adminSkippedBalancePayment && (
            <AdminActionButton
              adminAction={AdminActionEnum.SKIP_BALANCE_PAYMENT}
              onClickConfirm={() =>
                adminSkippedBalancePayment.callback({
                  clientProposalId: clientProposal._id,
                })
              }
              isLoading={adminSkippedBalancePayment.isLoading}
            />
          )}
      </div>
    </>
  )
}

export default UserCanPayBalanceFooter
